import React from 'react';

import { Text } from 'recharts';

export interface IPolarAngleAxisTickOptions {
  /**
   * Tilt for labels on mostly horizontal sectors (east/west), in degrees.
   * Applied as + for west (left) and − for east (right), per Recharts convention.
   */
  sideTiltDegrees?: number;
  /**
   * Extra SVG `dy` for side-tilted labels only; positive shifts text down.
   */
  sideDy?: number;
}

/**
 * PolarAngleAxis tick: tilts left/right labels so long words fit better on narrow screens.
 * Uses the same angle convention as Recharts (see PolarAngleAxis getTickTextAnchor).
 */
export function createPolarAngleAxisTick(
  options: IPolarAngleAxisTickOptions = {},
) {
  const { sideTiltDegrees = 26, sideDy } = options;

  return function renderPolarAngleAxisTick(props: Record<string, unknown>) {
    const { payload, dy: incomingDy, ...textProps } = props;
    const tickPayload = payload as
      | { coordinate?: number; value?: unknown }
      | undefined;
    const coordinate = tickPayload?.coordinate ?? 0;
    const label = tickPayload?.value != null ? String(tickPayload.value) : '';

    const rad = (-coordinate * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const isSideSector = Math.abs(cos) > Math.abs(sin) && Math.abs(cos) > 0.55;

    let angle = 0;
    let dy: number | undefined =
      typeof incomingDy === 'number' ? incomingDy : undefined;

    if (isSideSector) {
      angle = cos > 0 ? -sideTiltDegrees : sideTiltDegrees;
      if (sideDy != null) {
        dy = (dy ?? 0) + sideDy;
      }
    }

    return (
      <Text
        {...(textProps as React.ComponentProps<typeof Text>)}
        angle={angle}
        dy={dy}>
        {label}
      </Text>
    );
  };
}

/** Default tilt for muscle-group radar (first chart). */
export const renderPolarAngleAxisTick = createPolarAngleAxisTick();

/** Skills radar: vertical side labels + slight shift down. */
export const renderSkillsPolarAngleAxisTick = createPolarAngleAxisTick({
  sideTiltDegrees: 90,
  sideDy: 30,
});
